import { db } from '../firebase';
import { doc, setDoc, increment, serverTimestamp } from 'firebase/firestore';
import { isTrackingEnabled } from './env';

export { isTrackingEnabled };

// Self-owned usage metrics, written to the app's own Firestore (letools-pg).
//
// This is deliberately server-side counting that SURVIVES the ad blockers and
// DNS/agency filters that block Google Analytics — it hits the same Firestore
// domain the Video Canvass already uses, which isn't on any tracker blocklist.
//
// Privacy: it only ever stores the tool's route + friendly name. No uploaded
// file, plate, image, or case data is written, and the canvass capability id is
// already sanitized out by the caller. Writes are fire-and-forget and can never
// block or break the UI (and queue offline, so "disable WiFi after load" still
// holds — only a bare tool-open counter is ever synced).

// The Firestore doc id for a tool, derived from its route. Exported so the
// dashboard can match usage docs back to tool cards.
export function keyFromPath(path) {
  const k = String(path || '').replace(/^\/+/, '').replace(/\/+/g, '_');
  return k || 'landing';
}

function todayKey() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// A page visit — someone opened the tool.
export function trackToolUse(path, tool) {
  if (!isTrackingEnabled()) return;
  try {
    const key = keyFromPath(path);
    const day = todayKey();

    // Per-tool running total + last-used time.
    setDoc(doc(db, 'toolUsage', key), {
      tool: tool || key,
      path: String(path || ''),
      count: increment(1),
      lastUsedAt: serverTimestamp(),
    }, { merge: true }).catch(() => {});

    // Per-tool, per-day count (time-series metrics).
    setDoc(doc(db, 'toolUsage', key, 'daily', day), {
      date: day,
      count: increment(1),
    }, { merge: true }).catch(() => {});
  } catch {
    // Metrics must never break the app.
  }
}

// An actual interaction — someone pressed the button that makes the tool work
// (analyze, convert, parse, generate, …). Counted separately from visits so you
// can see how many visitors really used a tool. `toolKey` is the tool's route
// (e.g. '/lprpattern'); it shares the same toolUsage/{tool} document as visits.
export function trackToolAction(toolKey, tool) {
  if (!isTrackingEnabled()) return;
  try {
    const key = keyFromPath(toolKey);
    const day = todayKey();

    setDoc(doc(db, 'toolUsage', key), {
      tool: tool || key,
      path: String(toolKey || ''),
      runs: increment(1),
      lastRunAt: serverTimestamp(),
    }, { merge: true }).catch(() => {});

    setDoc(doc(db, 'toolUsage', key, 'daily', day), {
      date: day,
      runs: increment(1),
    }, { merge: true }).catch(() => {});
  } catch {
    // Metrics must never break the app.
  }
}
